import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Case } from "../types/case";
import type {
  DashboardAnalytics,
  DashboardAnalyticsApi,
  DashboardAnalyticsOptions,
  DashboardAnalyticsParams,
  DashboardCaseRow,
} from "../types/dashboard";
import { OPEN_CASE_STATES } from "../types/case";
import {
  computeStatusMetrics,
  computePriorityBreakdown,
  computeResolvedToday,
  computeSLACompliance,
  computeAvgResolutionTime,
  computeOverdueAgingBands,
  computeUnassignedCases,
  computeAssigneeWorkload,
  computeComplianceFunnel,
  computeDueDateForecast,
  computeThroughputTrend,
  computeTopRulesByVolume,
  computeChapterVolume,
  computeChapterCompletion,
  computeRuleSetGroups,
  computeRejectionRate,
  computeCreationTypeBreakdown,
  computeRiskExposure,
  computeUrgencyMatrix,
  computeStaleCases,
  computePriorityStatusHeatmap,
} from "../utils/dashboardCalculations";

export function useDashboardAnalytics(
  cases: Case[],
  options: DashboardAnalyticsOptions = {},
): DashboardAnalytics {
  const {
    getUserDisplayName,
    rules,
    throughputGranularity = "weekly",
    staleThresholdDays = 14,
    topRulesLimit = 10,
  } = options;

  return useMemo<DashboardAnalytics>(() => {
    const openStatesSet = new Set<number>(OPEN_CASE_STATES);
    const openCases = cases.filter((c) => openStatesSet.has(c.state as number));
    const statusMetrics = computeStatusMetrics(cases);
    const priorityBreakdown = computePriorityBreakdown(openCases);
    const resolvedToday = computeResolvedToday(cases);
    const slaCompliance = computeSLACompliance(cases);
    const avgResolutionTime = computeAvgResolutionTime(cases);
    const overdueAgingBands = computeOverdueAgingBands(cases);
    const unassignedCases = computeUnassignedCases(cases);
    const assigneeWorkload = computeAssigneeWorkload(cases, getUserDisplayName);
    const complianceFunnel = computeComplianceFunnel(cases);
    const dueDateForecast = computeDueDateForecast(cases);
    const throughputTrend = computeThroughputTrend(
      cases,
      throughputGranularity,
    );
    const topRulesByVolume = computeTopRulesByVolume(
      cases,
      rules,
      topRulesLimit,
    );
    const chapterVolume = computeChapterVolume(cases, rules, topRulesLimit);
    const chapterCompletion = computeChapterCompletion(
      cases,
      rules,
      topRulesLimit,
    );
    const ruleSetGroups = computeRuleSetGroups(cases, rules);
    const rejectionRate = computeRejectionRate(cases);
    const creationTypeBreakdown = computeCreationTypeBreakdown(cases);
    const riskExposure = computeRiskExposure(cases);
    const urgencyMatrix = computeUrgencyMatrix(cases);
    const staleCases = computeStaleCases(cases, staleThresholdDays);
    const priorityStatusHeatmap = computePriorityStatusHeatmap(cases);

    return {
      statusMetrics,
      priorityBreakdown,
      resolvedToday,
      slaCompliance,
      avgResolutionTime,
      overdueAgingBands,
      unassignedCases,
      assigneeWorkload,
      complianceFunnel,
      dueDateForecast,
      throughputTrend,
      topRulesByVolume,
      chapterVolume,
      chapterCompletion,
      ruleSetGroups,
      rejectionRate,
      creationTypeBreakdown,
      riskExposure,
      urgencyMatrix,
      staleCases,
      priorityStatusHeatmap,
    };
  }, [
    cases,
    getUserDisplayName,
    rules,
    throughputGranularity,
    staleThresholdDays,
    topRulesLimit,
  ]);
}

// ---------------------------------------------------------------------------
// API-backed hook — calls GET /api/case-management/cases/dashboard/analytics
// ---------------------------------------------------------------------------

import { casesApi } from "../api/cases";
import { queryKeys } from "../api/queryKeys";
import { useTenantId } from "./useTenantId";

export function useDashboardAnalyticsApi(
  params: DashboardAnalyticsParams = {},
  options?: { enabled?: boolean },
) {
  const tenantId = useTenantId();

  return useQuery<DashboardAnalyticsApi>({
    queryKey: queryKeys.cases.dashboardAnalytics(tenantId),
    queryFn: () => casesApi.getDashboardAnalytics(params),
    enabled: options?.enabled !== false,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Lightweight case rows hook — GET /api/case-management/cases/dashboard/cases
// ---------------------------------------------------------------------------

type DashboardCasesFilters = Parameters<typeof casesApi.getDashboardCases>[0];

export function useDashboardCases(
  filters: DashboardCasesFilters = {},
  options?: { enabled?: boolean },
) {
  const tenantId = useTenantId();
  // Stable key — only re-fetch when filters actually change
  const filtersKey = useMemo(
    () => filters as Record<string, unknown>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(filters)],
  );

  return useQuery<{
    data: DashboardCaseRow[];
    totalElements: number;
    totalPages: number;
    page: number;
    pageSize: number;
  }>({
    queryKey: queryKeys.cases.dashboardCases(tenantId, filtersKey),
    queryFn: () => casesApi.getDashboardCases(filters),
    enabled: options?.enabled !== false,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}
